/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  createAnalytics,
  createWebEventFactory,
  createEventStreamClient,
  createEventStreamSink,
  consoleSink,
  Sink,
  EventStreamEndpoint,
  EventInfoType} from '@amzn/ring-web-analytics';
import { config } from '../config';
import { RWACustomDimensions } from '../types';
import { getLanguage, isLocal, metricEvents } from '../utils';

const {
  RWA_CLIENT_ID,
  RWA_AWS_REGION,
  RWA_EVENT_STREAM_ENDPOINT, 
  RWA_GROUP,
  RWA_SUBGROUP
} = config;

export const useRWA = () => {
  const untrackedMetricsRef = useRef<
    { e: string; properties?: Record<string, unknown> }[]
  >([]);

  const track = (
    e: string,
    properties?: Record<string, unknown>
  ): void => {
    if (!window.RWA) {
      untrackedMetricsRef.current = [
        ...untrackedMetricsRef.current,
        { e, properties },
      ];
      return;
    }

    if (e === metricEvents.pageView && properties?.pageName) {
      window.RWA.createPageView({
        activator: 'push',
        title: properties?.pageName as string,
      });
    } else {
        window.RWA.recordEvent({
          type: (e === metricEvents.appLoad && 'init') || 'click' as EventInfoType,      
          resourceName: JSON.stringify(properties || {}), 
          resourceId: e
        });
    }
  };

  const buildSink = (endpoint: EventStreamEndpoint, isLocalStage: boolean): Sink => {
    if (isLocalStage) {
      return consoleSink;
    }

    return createEventStreamSink(createEventStreamClient({
      window,
      endpoint,
      group: RWA_GROUP,
      subgroup: RWA_SUBGROUP
    } as any));
  };

  const load = useCallback(() => {
    if (window.RWA) {
      return;
    }

    const locale = getLanguage() || 'en-US';
    const sessionId = sessionStorage.getItem('rwa_session_id') || uuidv4();
    const anonymousId = sessionStorage.getItem('rwa_anonymous_id') || uuidv4();
    
    // Store IDs for future use
    sessionStorage.setItem('rwa_session_id', sessionId);
    sessionStorage.setItem('rwa_anonymous_id', anonymousId);

    (window as any).RWA = createAnalytics<RWACustomDimensions>({
      window,
      clientId: RWA_CLIENT_ID,
      awsRegion: RWA_AWS_REGION,
      sessionId,
      uuidGenerator: () => uuidv4(),
      anonymousId,
      referrer: window.document.referrer,
      sink: buildSink(RWA_EVENT_STREAM_ENDPOINT as EventStreamEndpoint, isLocal),
      eventFactory: createWebEventFactory<RWACustomDimensions>({
        makeContext: (dimensions) => {
          return {
            locale: dimensions.locale,
            productName: dimensions.productName
          };
        }
      }),
      customDimensions: {
        locale
      }
    });
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | number;
    if (!window?.RWA) {
      intervalId = setInterval(() => {
        if (window?.RWA) {
          clearInterval(intervalId as NodeJS.Timeout);
          untrackedMetricsRef.current?.forEach(({ e, properties }) =>
            track(e, properties)
          );
          untrackedMetricsRef.current = [];
        }
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId as NodeJS.Timeout);
      }
    };
  }, [track]);

  return {
    load,
    track
  };
};